import React, { useEffect, useState } from 'react';
import { apiService } from '../services/api.service';

export function CustomerCompliance() {
  const [blocks, setBlocks] = useState<any[]>([]);
  const [bureauQueries, setBureauQueries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingOn, setActingOn] = useState<string | null>(null);
  const [docSearch, setDocSearch] = useState('');
  const [blockForm, setBlockForm] = useState({ documentNumber: '', reason: '', notes: '' });
  const [bureauDoc, setBureauDoc] = useState('');
  const [bureauProvider, setBureauProvider] = useState('VERAZ');
  const [lastBureau, setLastBureau] = useState<any | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [b, q] = await Promise.all([
        apiService.listCustomerBlocks(),
        apiService.listBureauQueries(docSearch || undefined),
      ]);
      setBlocks(b);
      setBureauQueries(q);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const createBlock = async (e: React.FormEvent) => {
    e.preventDefault();
    setActingOn('block');
    setError(null);
    try {
      await apiService.createCustomerBlock({
        documentNumber: blockForm.documentNumber,
        reason: blockForm.reason,
        notes: blockForm.notes || undefined,
      });
      setBlockForm({ documentNumber: '', reason: '', notes: '' });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setActingOn(null);
    }
  };

  const liftBlock = async (id: string) => {
    setActingOn(id);
    try {
      await apiService.liftCustomerBlock(id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setActingOn(null);
    }
  };

  const queryBureau = async (e: React.FormEvent) => {
    e.preventDefault();
    setActingOn('bureau');
    setError(null);
    try {
      const result = await apiService.queryBureau(bureauDoc, bureauProvider);
      setLastBureau(result);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setActingOn(null);
    }
  };

  const searchBureau = async () => {
    setLoading(true);
    try {
      setBureauQueries(await apiService.listBureauQueries(docSearch || undefined));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Bloqueos y buró</h1>
        <p className="text-sm text-gray-500 mt-1">Control de morosidad y consultas buró (Veraz, Riesgo OnLine)</p>
      </div>

      {error && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Nuevo bloqueo</h2>
          <form onSubmit={createBlock} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Documento</label>
              <input
                required
                value={blockForm.documentNumber}
                onChange={(e) => setBlockForm({ ...blockForm, documentNumber: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Motivo</label>
              <input
                required
                value={blockForm.reason}
                onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
              <textarea
                value={blockForm.notes}
                onChange={(e) => setBlockForm({ ...blockForm, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <button
              type="submit"
              disabled={actingOn === 'block'}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              {actingOn === 'block' ? 'Guardando…' : 'Bloquear documento'}
            </button>
          </form>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Consulta buró</h2>
          <form onSubmit={queryBureau} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Documento</label>
              <input
                required
                value={bureauDoc}
                onChange={(e) => setBureauDoc(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Proveedor</label>
              <select
                value={bureauProvider}
                onChange={(e) => setBureauProvider(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="VERAZ">Veraz</option>
                <option value="RIESGO_ONLINE">Riesgo OnLine</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={actingOn === 'bureau'}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {actingOn === 'bureau' ? 'Consultando…' : 'Consultar buró'}
            </button>
          </form>
          {lastBureau && (
            <div className="mt-4 p-3 bg-blue-50 rounded-md text-sm">
              <p><strong>Score:</strong> {lastBureau.score}</p>
              <p><strong>Riesgo:</strong> {lastBureau.result?.riskLevel ?? '—'}</p>
              {lastBureau.result?.message && (
                <p className="text-gray-600 mt-1">{lastBureau.result.message}</p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Bloqueos activos</h2>
        {loading ? (
          <p className="text-gray-500">Cargando…</p>
        ) : blocks.length === 0 ? (
          <p className="text-gray-500">Sin bloqueos activos.</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Documento</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Motivo</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Acción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {blocks.map((b) => (
                <tr key={b.id}>
                  <td className="px-4 py-2 text-sm">{b.documentNumber}</td>
                  <td className="px-4 py-2 text-sm">{b.reason}</td>
                  <td className="px-4 py-2 text-sm">{new Date(b.blockedAt).toLocaleDateString()}</td>
                  <td className="px-4 py-2 text-sm text-right">
                    <button
                      disabled={actingOn === b.id}
                      onClick={() => liftBlock(b.id)}
                      className="text-green-600 hover:underline disabled:opacity-50"
                    >
                      Levantar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Historial buró</h2>
          <div className="flex gap-2">
            <input
              placeholder="Filtrar por documento"
              value={docSearch}
              onChange={(e) => setDocSearch(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            />
            <button onClick={searchBureau} className="text-sm text-blue-600 hover:underline">Buscar</button>
          </div>
        </div>
        {bureauQueries.length === 0 ? (
          <p className="text-gray-500">Sin consultas.</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Documento</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Proveedor</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {bureauQueries.map((q) => (
                <tr key={q.id}>
                  <td className="px-4 py-2 text-sm">{q.documentNumber}</td>
                  <td className="px-4 py-2 text-sm">{q.score ?? '—'}</td>
                  <td className="px-4 py-2 text-sm">{q.provider}</td>
                  <td className="px-4 py-2 text-sm">{new Date(q.queriedAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
